import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Loader2, Search, ShieldCheck, UserRound, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api, ApiError, formatApiError } from "../api";
import { useAuth } from "../auth";
import { AppHeader, AppStateScreen, BrandedLoading, ProfilePhoto } from "../components/app";
import type {
  AdminUserListItem,
  ManualMatchMemberSummary,
  ManualMatchPreview,
  ManualMatchQueueItem,
} from "../types";
import { RequireAdmin } from "./AdminPages";

type PickedMember = Pick<
  AdminUserListItem,
  "id" | "email" | "firstName" | "profilePictureId"
>;

function memberName(member: PickedMember | ManualMatchMemberSummary | null) {
  return member?.firstName || member?.email || "Unavailable member";
}

function previewFromApiError(error: unknown): ManualMatchPreview | null {
  if (!(error instanceof ApiError)) return null;
  const data = error.data as { preview?: ManualMatchPreview } | undefined;
  return data?.preview ?? null;
}

function MemberAvatar({
  member,
}: {
  member: PickedMember | ManualMatchMemberSummary;
}) {
  return (
    <span className="manual-match-avatar" aria-hidden="true">
      <ProfilePhoto photoId={member.profilePictureId} />
    </span>
  );
}

function MemberPicker({
  label,
  selected,
  excludeId,
  disabled = false,
  onSelect,
}: {
  label: string;
  selected: PickedMember | null;
  excludeId?: string;
  disabled?: boolean;
  onSelect: (member: PickedMember | null) => void;
}) {
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim();
  const query = useQuery({
    queryKey: ["admin-manual-match-member-search", normalizedSearch],
    queryFn: () => api.getAdminUsers({ filter: "all", search: normalizedSearch }),
    enabled: normalizedSearch.length >= 2 && !selected,
  });
  const results = (query.data?.users ?? [])
    .filter((member) => member.id !== excludeId)
    .slice(0, 8);

  return (
    <div className="manual-member-picker">
      <span className="manual-field-label">{label}</span>
      {selected ? (
        <div className="manual-selected-member">
          <MemberAvatar member={selected} />
          <span>
            <strong>{memberName(selected)}</strong>
            <small>{selected.email}</small>
          </span>
          <button
            type="button"
            disabled={disabled}
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={() => {
              onSelect(null);
              setSearch("");
            }}
          >
            <X size={17} aria-hidden="true" />
          </button>
        </div>
      ) : (
        <>
          <label className="manual-member-search">
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              disabled={disabled}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name or email"
              aria-label={`${label}: search by name or email`}
            />
            {query.isFetching && <Loader2 className="spin" size={17} aria-hidden="true" />}
          </label>
          {normalizedSearch.length > 0 && normalizedSearch.length < 2 && (
            <small className="manual-picker-hint">Enter at least two characters.</small>
          )}
          {query.error && (
            <small className="manual-picker-error">{formatApiError(query.error)}</small>
          )}
          {normalizedSearch.length >= 2 && !query.isFetching && !query.error && results.length === 0 && (
            <small className="manual-picker-hint">No matching members.</small>
          )}
          {results.length > 0 && (
            <div className="manual-member-results" role="listbox" aria-label={`${label} search results`}>
              {results.map((member) => (
                <button
                  type="button"
                  disabled={disabled}
                  role="option"
                  aria-selected="false"
                  key={member.id}
                  onClick={() => {
                    onSelect(member);
                    setSearch("");
                  }}
                >
                  <MemberAvatar member={member} />
                  <span>
                    <strong>{memberName(member)}</strong>
                    <small>{member.email}</small>
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PreviewResult({ preview }: { preview: ManualMatchPreview }) {
  return (
    <div className={`manual-preview ${preview.canQueue ? "is-ready" : "is-blocked"}`} role="status">
      <div className="manual-preview-heading">
        {preview.canQueue
          ? <CheckCircle2 size={20} aria-hidden="true" />
          : <AlertTriangle size={20} aria-hidden="true" />}
        <strong>{preview.canQueue ? "Pair is eligible" : "Pair cannot be matched"}</strong>
        {preview.compatibilityScore !== null && (
          <span>Score {preview.compatibilityScore.toFixed(1)}</span>
        )}
      </div>
      {preview.bypassesAutomatedThreshold && (
        <p>
          This score is below the automated threshold. Admin placement may bypass that score,
          while strict mutual preferences and all account safeguards still apply.
        </p>
      )}
      {preview.issues.length > 0 && (
        <ul>
          {preview.issues.map((issue) => <li key={issue.code}>{issue.message}</li>)}
        </ul>
      )}
    </div>
  );
}

function formatQueueDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown time" : date.toLocaleString();
}

function statusLabel(status: ManualMatchQueueItem["status"]) {
  if (status === "consumed") return "Introduced";
  if (status === "blocked") return "Blocked";
  if (status === "cancelled") return "Cancelled";
  return "Queued";
}

function QueueCard({
  entry,
  cancelling,
  onCancel,
}: {
  entry: ManualMatchQueueItem;
  cancelling: boolean;
  onCancel: () => void;
}) {
  const [first, second] = entry.members;
  return (
    <article className={`manual-queue-card manual-queue-card--${entry.status}`}>
      <div className="manual-queue-card-head">
        <span className={`manual-queue-status manual-queue-status--${entry.status}`}>
          {statusLabel(entry.status)}
        </span>
        <time dateTime={entry.addedAt}>{formatQueueDate(entry.addedAt)}</time>
      </div>
      <div className="manual-queue-pair">
        {[first, second].map((member, index) => (
          <div key={member?.id ?? index}>
            {member
              ? <MemberAvatar member={member} />
              : (
                <span className="manual-match-avatar" aria-hidden="true">
                  <ProfilePhoto />
                </span>
              )}
            <span>
              <strong>{memberName(member)}</strong>
              {member?.email && <small>{member.email}</small>}
            </span>
          </div>
        ))}
      </div>
      {entry.adminNote && <p className="manual-queue-note"><strong>Admin note:</strong> {entry.adminNote}</p>}
      {entry.blockedReason && <p className="manual-queue-blocked">{entry.blockedReason}</p>}
      <div className="manual-queue-meta">
        <span>Requested by {entry.requestedByEmail || "an admin"}</span>
        {entry.status === "queued" && (
          <button
            type="button"
            className="text-link"
            disabled={cancelling}
            onClick={onCancel}
          >
            {cancelling ? "Removing…" : "Remove from queue"}
          </button>
        )}
      </div>
    </article>
  );
}

export function ManualMatchingPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialMemberId = searchParams.get("member") || "";
  const queryClient = useQueryClient();
  const [first, setFirst] = useState<PickedMember | null>(null);
  const [second, setSecond] = useState<PickedMember | null>(null);
  const [mode, setMode] = useState<"queue" | "immediate">("queue");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState<ManualMatchPreview | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const initialMemberApplied = useRef(false);

  const initialMember = useQuery({
    queryKey: ["admin-user", initialMemberId],
    queryFn: () => api.getAdminUser(initialMemberId),
    enabled: user?.role === "admin" && Boolean(initialMemberId),
  });
  useEffect(() => {
    if (!initialMember.data || initialMemberApplied.current) return;
    initialMemberApplied.current = true;
    setFirst({
      id: initialMember.data.user.id,
      email: initialMember.data.user.email,
      firstName: initialMember.data.profile?.firstName ?? null,
      profilePictureId: initialMember.data.profile?.profilePictureId ?? null,
    });
  }, [initialMember.data]);

  const queueQuery = useQuery({
    queryKey: ["admin-manual-match-queue"],
    queryFn: api.getManualMatchQueue,
    enabled: user?.role === "admin",
  });
  const previewMutation = useMutation({
    mutationFn: ({ userId, partnerUserId }: { userId: string; partnerUserId: string }) =>
      api.previewManualMatch(userId, partnerUserId),
    onSuccess: ({ preview: nextPreview }) => {
      setPreview(nextPreview);
      setNotice(null);
    },
  });
  const queueMutation = useMutation({
    mutationFn: api.queueManualMatch,
    onSuccess: () => {
      setFirst(null);
      setSecond(null);
      setNote("");
      setPreview(null);
      setNotice("The pair is queued for the next matching round.");
      queryClient.invalidateQueries({ queryKey: ["admin-manual-match-queue"] });
    },
    onError: (error) => {
      const nextPreview = previewFromApiError(error);
      if (nextPreview) setPreview(nextPreview);
    },
  });
  const immediateMutation = useMutation({
    mutationFn: api.createManualMatchImmediately,
    onSuccess: () => {
      setFirst(null);
      setSecond(null);
      setNote("");
      setPreview(null);
      setNotice("The introduction was created and the standard notification flow has started.");
      queryClient.invalidateQueries({ queryKey: ["admin-manual-match-queue"] });
    },
    onError: (error) => {
      const nextPreview = previewFromApiError(error);
      if (nextPreview) setPreview(nextPreview);
    },
  });
  const cancelMutation = useMutation({
    mutationFn: api.cancelManualMatch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-manual-match-queue"] });
    },
  });

  const entries = useMemo(() => {
    const rows = queueQuery.data?.entries ?? [];
    return [...rows].sort((left, right) => {
      if (left.status === "queued" && right.status !== "queued") return -1;
      if (left.status !== "queued" && right.status === "queued") return 1;
      return new Date(right.addedAt).getTime() - new Date(left.addedAt).getTime();
    });
  }, [queueQuery.data?.entries]);

  const selectFirst = (member: PickedMember | null) => {
    previewMutation.reset();
    queueMutation.reset();
    immediateMutation.reset();
    setFirst(member);
    setPreview(null);
    setNotice(null);
  };
  const selectSecond = (member: PickedMember | null) => {
    previewMutation.reset();
    queueMutation.reset();
    immediateMutation.reset();
    setSecond(member);
    setPreview(null);
    setNotice(null);
  };
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!first || !second) return;
    if (!preview) {
      previewMutation.mutate({ userId: first.id, partnerUserId: second.id });
      return;
    }
    if (!preview.canQueue) return;
    const confirmed = mode === "queue"
      ? window.confirm(
          `Queue ${memberName(first)} and ${memberName(second)} for the next matching round?`,
        )
      : window.confirm(
          `Create an introduction for ${memberName(first)} and ${memberName(second)} now? This starts the standard response and notification flow immediately.`,
        );
    if (confirmed) {
      const request = {
        userId: first.id,
        partnerUserId: second.id,
        adminNote: note.trim() || undefined,
      };
      if (mode === "queue") queueMutation.mutate(request);
      else immediateMutation.mutate(request);
    }
  };

  const isSubmitting = queueMutation.isPending || immediateMutation.isPending;
  const isFormBusy = previewMutation.isPending || isSubmitting;
  const actionError = mode === "queue" ? queueMutation.error : immediateMutation.error;

  return (
    <RequireAdmin>
      <AppHeader title="Manual matching" backTo="/settings" historyBack />
      <div className="settings-document-scroll manual-matching-document-scroll">
        <main className="manual-matching-page">
          <section className="manual-match-intro">
            <span className="manual-match-intro-icon"><ShieldCheck aria-hidden="true" /></span>
            <div>
              <p className="eyebrow">Admin operations</p>
              <h1>Create a specific introduction</h1>
              <p>
                Choose whether to reserve two eligible members for the next round or introduce them
                now. Members experience the standard flow; no manual label, score, or admin note is shown.
              </p>
            </div>
          </section>

          <form className="manual-match-form" onSubmit={onSubmit}>
            <div className="manual-match-form-head">
              <div>
                <p className="eyebrow">New manual introduction</p>
                <h2>Select two members</h2>
              </div>
              <span>Admin only</span>
            </div>
            <div className="manual-picker-grid">
              <MemberPicker
                label="First member"
                selected={first}
                excludeId={second?.id}
                disabled={isFormBusy}
                onSelect={selectFirst}
              />
              <MemberPicker
                label="Second member"
                selected={second}
                excludeId={first?.id}
                disabled={isFormBusy}
                onSelect={selectSecond}
              />
            </div>
            <fieldset className="manual-match-mode">
              <legend>When should this introduction happen?</legend>
              <div className="manual-match-mode-options">
                <label>
                  <input
                    type="radio"
                    name="manual-match-mode"
                    value="queue"
                    checked={mode === "queue"}
                    disabled={isFormBusy}
                    onChange={() => {
                      queueMutation.reset();
                      immediateMutation.reset();
                      setMode("queue");
                      setNotice(null);
                    }}
                  />
                  <span>
                    <strong>Add to queue</strong>
                    <small>Reserve this pair for the next matching round.</small>
                  </span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="manual-match-mode"
                    value="immediate"
                    checked={mode === "immediate"}
                    disabled={isFormBusy}
                    onChange={() => {
                      queueMutation.reset();
                      immediateMutation.reset();
                      setMode("immediate");
                      setNotice(null);
                    }}
                  />
                  <span>
                    <strong>Match immediately</strong>
                    <small>Create the introduction now and notify the first responder.</small>
                  </span>
                </label>
              </div>
            </fieldset>
            <label className="field manual-admin-note">
              <span>Admin note <small>optional · never shown to members</small></span>
              <textarea
                rows={3}
                maxLength={500}
                disabled={isFormBusy}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Why this pair is being selected"
              />
              <small>{note.length} / 500</small>
            </label>

            {preview && <PreviewResult preview={preview} />}
            {notice && <div className="banner success" role="status">{notice}</div>}
            {(previewMutation.error || actionError) && (
              <div className="banner error" role="alert">
                {formatApiError(previewMutation.error || actionError)}
              </div>
            )}

            <button
              type="submit"
              className="button primary manual-match-submit"
              disabled={!first || !second || previewMutation.isPending || isSubmitting || Boolean(preview && !preview.canQueue)}
            >
              {previewMutation.isPending || isSubmitting
                ? <Loader2 className="spin" size={18} aria-hidden="true" />
                : preview?.canQueue
                  ? <CheckCircle2 size={18} aria-hidden="true" />
                  : <Search size={18} aria-hidden="true" />}
              {queueMutation.isPending
                ? "Adding to queue…"
                : immediateMutation.isPending
                  ? "Creating introduction…"
                : previewMutation.isPending
                  ? "Checking pair…"
                  : preview?.canQueue
                    ? mode === "queue" ? "Add to matching queue" : "Match immediately"
                    : "Check pair"}
            </button>
          </form>

          <section className="manual-queue-section">
            <div className="manual-queue-section-head">
              <div>
                <p className="eyebrow">Matching queue</p>
                <h2>Manual pair history</h2>
              </div>
              <span>{entries.filter((entry) => entry.status === "queued").length} queued</span>
            </div>
            {queueQuery.isLoading ? (
              <BrandedLoading message="Loading manual queue" />
            ) : queueQuery.error ? (
              <div className="banner error" role="alert">
                {formatApiError(queueQuery.error)}
                <button type="button" className="text-link" onClick={() => queueQuery.refetch()}>
                  Try again
                </button>
              </div>
            ) : entries.length === 0 ? (
              <AppStateScreen
                icon={UserRound}
                title="No manual pairs"
                body="Manual introductions created or queued by an admin will appear here."
                tone="neutral"
              />
            ) : (
              <div className="manual-queue-list">
                {entries.map((entry) => (
                  <QueueCard
                    key={entry.id}
                    entry={entry}
                    cancelling={cancelMutation.isPending && cancelMutation.variables === entry.id}
                    onCancel={() => {
                      if (window.confirm("Remove this pair from the next matching round?")) {
                        cancelMutation.mutate(entry.id);
                      }
                    }}
                  />
                ))}
              </div>
            )}
            {cancelMutation.error && (
              <div className="banner error" role="alert">{formatApiError(cancelMutation.error)}</div>
            )}
          </section>
        </main>
      </div>
    </RequireAdmin>
  );
}
